import { useCallback, useEffect, useRef, useState } from 'react';
import { Room } from 'livekit-client';
import { LiveKitService } from '../services/LiveKitService';
import { LiveKitConnectionOptions, LiveKitEvents, LiveKitMode, LiveKitState } from '../types';

/**
 * Hook for managing LiveKit video connections
 */
export const useLiveKit = (events?: LiveKitEvents) => {
  const [state, setState] = useState<LiveKitState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<string[]>([]);
  const [room, setRoom] = useState<Room | null>(null);
  const [currentMode, setCurrentMode] = useState<LiveKitMode | null>(null);

  const serviceRef = useRef<LiveKitService | null>(null);

  /**
   * Connect to a LiveKit room
   */
  const connect = useCallback(
    async (roomName: string, participantName: string, mode: LiveKitMode) => {
      try {
        if (serviceRef.current) {
          await serviceRef.current.disconnect();
        }

        const service = new LiveKitService({
          onStateChange: (newState) => {
            setState(newState);
            events?.onStateChange?.(newState);
          },
          onError: (livekitError) => {
            setError(livekitError.message);
            events?.onError?.(livekitError);
          },
          onParticipantJoined: (participantId) => {
            setParticipants((prev) => [...prev, participantId]);
            events?.onParticipantJoined?.(participantId);
          },
          onParticipantLeft: (participantId) => {
            setParticipants((prev) => prev.filter((id) => id !== participantId));
            events?.onParticipantLeft?.(participantId);
          },
        });

        const options: LiveKitConnectionOptions = {
          roomName,
          participantName,
          mode,
        };

        const result = await service.connect(options);

        if (result.success) {
          serviceRef.current = service;
          setRoom(result.room || null);
          setError(null);
        } else {
          setError(result.error || 'Failed to connect');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to connect to LiveKit');
        throw err;
      }
    },
    [events]
  );

  /**
   * Disconnect from the room
   */
  const disconnect = useCallback(async () => {
    if (serviceRef.current) {
      await serviceRef.current.disconnect();
      serviceRef.current = null;
      setRoom(null);
      setParticipants([]);
      setError(null);
      setCurrentMode(null);
    }
  }, []);

  /**
   * Start streaming (connect as streamer)
   */
  const startStreaming = useCallback(
    async (roomName: string, participantName: string) => {
      setCurrentMode('stream');
      return connect(roomName, participantName, 'stream');
    },
    [connect]
  );

  /**
   * Join as viewer
   */
  const joinAsViewer = useCallback(
    async (roomName: string, participantName: string) => {
      setCurrentMode('join');
      return connect(roomName, participantName, 'join');
    },
    [connect]
  );

  /**
   * Get the current room
   */
  const getRoom = useCallback(() => {
    return serviceRef.current?.getRoom() || null;
  }, []);

  /**
   * Check if connected
   */
  const isConnected = state === 'connected';
  const isConnecting = state === 'connecting';
  const hasError = state === 'error';

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (serviceRef.current) {
        serviceRef.current.dispose();
        serviceRef.current = null;
      }
    };
  }, []);

  return {
    // Actions
    connect,
    disconnect,
    startStreaming,
    joinAsViewer,
    getRoom,

    // State
    state,
    error,
    participants,
    room,
    currentMode,
    isConnected,
    isConnecting,
    hasError,
  };
};
